/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  authAPI,
  setApiAccessToken,
  registerAuthErrorHandler,
} from '@/api/client'
import type { MeResponseDto } from '@/api/generated/api'

type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'hydrating'
  | 'ready'

interface AuthContextState {
  status: AuthStatus
  accessToken: string | null
  user: MeResponseDto | null
  expiresAt: Date | null
  tokenType: string | null
}

interface AuthHandlers {
  login: (options: {
    accessToken: string
    user: MeResponseDto
    expiresAt?: Date | null
    tokenType?: string | null
  }) => void
  logout: () => void
}

export type AuthContextType = AuthContextState & {
  isAuthenticated: boolean
  error: string | null
  session: {
    accessToken: string
    expiresAt?: Date
    tokenType?: string | null
  } | null
  sessionRemainingMs: number | null
  refreshSession: () => Promise<void>
} & AuthHandlers

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthContextState>({
    status: 'loading',
    accessToken: null,
    user: null,
    expiresAt: null,
    tokenType: null,
  })

  const handleAuthFailure = useCallback(() => {
    setState({
      status: 'unauthenticated',
      accessToken: null,
      user: null,
      expiresAt: null,
      tokenType: null,
    })
    setApiAccessToken(null)
  }, [])

  useEffect(() => {
    registerAuthErrorHandler(handleAuthFailure)
    return () => {
      registerAuthErrorHandler(null)
    }
  }, [handleAuthFailure])

  // Restore session from sessionStorage (E2E test support)
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const e2eToken = sessionStorage.getItem('__whiteGi_e2e_accessToken')
        if (e2eToken) {
          setApiAccessToken(e2eToken)
          const profile = await authAPI.getProfile()
          setState({
            status: 'authenticated',
            accessToken: e2eToken,
            user: profile.data,
            expiresAt: null,
            tokenType: 'access',
          })
          return
        }
      } catch (error) {
        console.error('Session restoration failed:', error)
      }

      // No valid session found
      setState(prev => ({
        ...prev,
        status: 'unauthenticated',
        expiresAt: null,
        tokenType: null,
      }))
    }

    void restoreSession()
  }, [])

  const login = useCallback(
    ({
      accessToken,
      user,
      expiresAt,
      tokenType,
    }: {
      accessToken: string
      user: MeResponseDto
      expiresAt?: Date | null
      tokenType?: string | null
    }) => {
      setState({
        status: 'authenticated',
        accessToken,
        user,
        expiresAt: expiresAt ?? null,
        tokenType: tokenType ?? 'access',
      })
      setApiAccessToken(accessToken)
      // Persist for E2E test navigation
      if (sessionStorage.getItem('__whiteGi_e2e_accessToken')) {
        sessionStorage.setItem('__whiteGi_e2e_accessToken', accessToken)
      }
    },
    []
  )

  const logout = useCallback(() => {
    setState({
      status: 'unauthenticated',
      accessToken: null,
      user: null,
      expiresAt: null,
      tokenType: null,
    })
    setApiAccessToken(null)
    sessionStorage.removeItem('__whiteGi_e2e_accessToken')
  }, [])

  const refreshSession = useCallback(async () => {
    // Placeholder for future refresh logic
    // For now, just check if session is still valid
    if (state.accessToken) {
      try {
        const profile = await authAPI.getProfile()
        setState(prev => ({ ...prev, user: profile.data }))
      } catch (error) {
        handleAuthFailure()
      }
    }
  }, [state.accessToken, handleAuthFailure])

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      isAuthenticated:
        state.status === 'authenticated' && Boolean(state.accessToken),
      error: null,
      session: state.accessToken
        ? {
            accessToken: state.accessToken,
            expiresAt: state.expiresAt ?? undefined,
            tokenType: state.tokenType ?? undefined,
          }
        : null,
      sessionRemainingMs:
        state.expiresAt && state.expiresAt.getTime() > Date.now()
          ? state.expiresAt.getTime() - Date.now()
          : null,
      refreshSession,
      login,
      logout,
    }),
    [state, login, logout, refreshSession]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
